import * as React from 'react';

export type CardLinkContextValue = {
  href: string;
};

const CardLinkContext = React.createContext<CardLinkContextValue | null>(null);

export function CardLinkProvider({
  value,
  children,
}: {
  value: CardLinkContextValue | null;
  children: React.ReactNode;
}) {
  return React.createElement(CardLinkContext.Provider, { value }, children);
}

export function useCardLinkContext(): CardLinkContextValue | null {
  return React.useContext(CardLinkContext);
}

/**
 * Compare hrefs for same-destination demotion of nested links/buttons.
 * Relative hrefs only match relative hrefs, and absolute hrefs must share an
 * origin, so a CTA to another site is never hidden behind the card link.
 */
export function cardLinkHrefsMatch(
  cardHref: string,
  childHref: string | undefined,
): boolean {
  if (!childHref) return false;
  const card = normalizeCardHref(cardHref);
  const child = normalizeCardHref(childHref);
  return card.origin === child.origin && card.location === child.location;
}

const RELATIVE_BASE = 'https://card-link.invalid';

function normalizeCardHref(href: string): {
  origin: string | null;
  location: string;
} {
  const trimmed = href.trim();
  if (!trimmed) return { origin: null, location: trimmed };
  try {
    const url = new URL(trimmed, RELATIVE_BASE);
    const isRelative = url.origin === RELATIVE_BASE;
    const path = url.pathname.replace(/\/$/, '') || '/';
    return {
      origin: isRelative ? null : url.origin,
      location: `${path}${url.search}${url.hash}`,
    };
  } catch {
    return { origin: null, location: trimmed.replace(/\/$/, '') || trimmed };
  }
}
